import { useCallback, useState } from "react";
import { validateInput, type FieldIssue, type ZodType } from "@app/core/validation";
import { useLang } from "../i18n.js";
import { ApiError } from "../api/client.js";
import { resolveApiError, resolveFieldErrors, type ResolvedError } from "./resolve.js";

export interface FormErrorsApi {
  /** Per-field messages, keyed by field path — pass straight to `<Input error={…}>`. */
  fieldErrors: Record<string, string>;
  /** A form-level (non-field) error to render with `<ErrorAlert>`, or null. */
  formError: ResolvedError | null;
  /**
   * Validate a value against a schema BEFORE hitting the network. On failure it
   * fills `fieldErrors`, focuses the first invalid field, and returns
   * `{ ok: false }`; on success it clears errors and returns `{ ok: true, data }`.
   */
  validate: <T>(schema: ZodType<T>, value: unknown) => { ok: true; data: T } | { ok: false };
  /**
   * Handle a thrown error from an API call: server field issues (from a 400
   * `details.issues`) drop into `fieldErrors`; everything else becomes
   * `formError`. Anything not an ApiError still resolves to a generic message.
   */
  handle: (err: unknown) => void;
  /** Clear all field + form errors (call at the start of a submit). */
  reset: () => void;
  /** Clear a single field's error (call from its onChange for live feedback). */
  clearField: (field: string) => void;
}

/** Focus the first control matching a field path, if the form labelled it. */
function focusField(field: string): void {
  if (typeof document === "undefined") return;
  const el = document.querySelector<HTMLElement>(`[name="${CSS.escape(field)}"]`);
  el?.focus();
}

/**
 * Form error state shared by every panel form. Keeps submit-time (zod) and
 * server-side validation in ONE field-error map so both surface identically
 * under the same inputs — the whole point of sharing `@app/core` schemas.
 */
export function useFormErrors(): FormErrorsApi {
  const { lang } = useLang();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<ResolvedError | null>(null);

  const reset = useCallback(() => {
    setFieldErrors({});
    setFormError(null);
  }, []);

  const clearField = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const applyIssues = useCallback(
    (issues: FieldIssue[]) => {
      const map = resolveFieldErrors(issues, lang);
      setFieldErrors(map);
      const first = issues.find((i) => i.field)?.field;
      if (first) focusField(first);
    },
    [lang],
  );

  const validate = useCallback(
    <T>(schema: ZodType<T>, value: unknown): { ok: true; data: T } | { ok: false } => {
      const result = validateInput(schema, value);
      if (result.ok) {
        reset();
        return { ok: true, data: result.data };
      }
      setFormError(null);
      applyIssues(result.issues);
      return { ok: false };
    },
    [applyIssues, reset],
  );

  const handle = useCallback(
    (err: unknown) => {
      const issues =
        err instanceof ApiError && err.details && typeof err.details === "object"
          ? (err.details as { issues?: FieldIssue[] }).issues
          : undefined;
      if (issues && issues.length > 0) {
        setFormError(null);
        applyIssues(issues);
        return;
      }
      setFieldErrors({});
      setFormError(resolveApiError(err, lang));
    },
    [applyIssues, lang],
  );

  return { fieldErrors, formError, validate, handle, reset, clearField };
}

/**
 * A localized one-liner resolver for toast messages — the `toast.error(...)`
 * equivalent of `<ErrorAlert>`. Returns just the message title (toasts have no
 * room for a hint or an action button).
 */
export function useErrorText(): (err: unknown) => string {
  const { lang } = useLang();
  return useCallback((err: unknown) => resolveApiError(err, lang).title, [lang]);
}
